<script setup lang="ts">
import { BoxComponent, type CreatorSegmentType, FlexComponent, InputComponent, type SegmentsFilter, Selector } from '@metapic-apps/shared'
import { ElIcon } from 'element-plus'
import debounce from 'lodash/debounce'
import { computed, ref } from 'vue'

import SearchIcon from '@/assets/icons/Magnifier.svg?component'

const TYPE_OPTIONS: { id: number; label: string; value?: CreatorSegmentType }[] = [
  { id: 0, label: 'All types' },
  { id: 1, label: 'Dynamic', value: 'dynamic' },
  { id: 2, label: 'Static', value: 'static' },
]

const filter = defineModel<SegmentsFilter>('modelValue', { required: true })
const searchInput = ref(filter.value.query ?? '')

const applySearch = debounce((value: string) => {
  filter.value = { ...filter.value, query: value || undefined }
}, 500)

const handleSearchInput = (value: string) => {
  searchInput.value = value
  applySearch(value)
}

const selectedTypeOption = computed(() => TYPE_OPTIONS.find((option) => option.value === filter.value.type) ?? TYPE_OPTIONS[0])
const typeLabel = computed(() => selectedTypeOption.value.label)
const typeSelectorOptions = computed(() => TYPE_OPTIONS.map(({ id, label }) => ({ id, label })))

const handleTypeChange = (option: { id: number } | null) => {
  const selected = option ? TYPE_OPTIONS.find((item) => item.id === option.id) : undefined
  filter.value = { ...filter.value, type: selected?.value }
}
</script>
<template>
  <BoxComponent>
    <FlexComponent justify="space-between" align="center" :gap="16">
      <FlexComponent wrap="wrap" align="center" :gap="8">
        <BoxComponent :w="240" class="segments-filters__search">
          <InputComponent :model-value="searchInput" placeholder="Search segments" size="large" @input="handleSearchInput">
            <template #prefix>
              <ElIcon :size="16">
                <SearchIcon />
              </ElIcon>
            </template>
          </InputComponent>
        </BoxComponent>
        <Selector
          :options="typeSelectorOptions"
          :selected-option="selectedTypeOption"
          :selected-label="typeLabel"
          :show-search="false"
          dropdown-label="Type"
          @update:selected-option="handleTypeChange"
        />
      </FlexComponent>
      <slot name="actions" />
    </FlexComponent>
  </BoxComponent>
</template>
<style lang="scss" scoped>
.segments-filters__search {
  :deep(.el-input__wrapper) {
    box-shadow: none;
    background: transparent;
    padding-inline: 0;
    border-bottom: 1px solid transparent;
    border-radius: 0;
    transition: border-color var(--transition) ease;
    &:hover {
      box-shadow: none;
    }
  }
  &:focus-within :deep(.el-input__wrapper) {
    border-bottom-color: var(--black, #000);
  }
}
</style>
